class HeaderComponent extends HTMLElement {
  #initialHeaderTop = 0;
  #scrollHandler = null;
  #resizeObserver = null;

  constructor() {
    super();

    this.menuToggle = null;
    this.menuClose = null;
    this.mobileMenuOverlay = null;
    this.mobileMenu = null;
    this.megamenuPanel = null;
    this.megamenuPanelBack = null;
    this.megamenuPanelContent = null;
  }

  connectedCallback() {
    this.menuToggle = this.querySelector('[data-ref="menu-toggle"]');
    this.menuClose = this.querySelector('[data-ref="menu-close"]');
    this.mobileMenuOverlay = this.querySelector('[data-ref="mobile-menu-overlay"]');
    this.mobileMenu = this.querySelector('[data-ref="mobile-menu"]');
    this.megamenuPanel = this.querySelector('[data-ref="megamenu-panel"]');
    this.megamenuPanelBack = this.querySelector('[data-ref="megamenu-panel-back"]');
    this.megamenuPanelContent = this.querySelector('[data-ref="megamenu-panel-content"]');

    if (this.megamenuPanelContent) {
      this.querySelectorAll('[data-ref="megamenu-content"]').forEach((el) => {
        this.megamenuPanelContent.appendChild(el);
      });
    }

    if (this.menuToggle) {
      this.menuToggle.addEventListener('click', this.#handleMenuOpen);
    }

    if (this.menuClose) {
      this.menuClose.addEventListener('click', this.#handleMenuClose);
    }

    if (this.mobileMenuOverlay) {
      this.mobileMenuOverlay.addEventListener('click', this.#handleOverlayClick);
    }

    this.querySelectorAll('[data-ref="megamenu-entry"]').forEach((entry) => {
      entry.addEventListener('click', this.#handleMegamenuEntryClick);
    });

    if (this.megamenuPanelBack) {
      this.megamenuPanelBack.addEventListener('click', this.#handleMegamenuPanelBack);
    }

    document.addEventListener('keydown', this.#handleKeyDown);

    // Initialiser l'observation sticky si le header est sticky
    this.#initStickyObserver();

    this.updateHeaderOffsetHeight();
    this.#resizeObserver = new ResizeObserver(() => this.updateHeaderOffsetHeight());
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    if (this.menuToggle) {
      this.menuToggle.removeEventListener('click', this.#handleMenuOpen);
    }

    if (this.menuClose) {
      this.menuClose.removeEventListener('click', this.#handleMenuClose);
    }

    if (this.mobileMenuOverlay) {
      this.mobileMenuOverlay.removeEventListener('click', this.#handleOverlayClick);
    }

    this.querySelectorAll('[data-ref="megamenu-entry"]').forEach((entry) => {
      entry.removeEventListener('click', this.#handleMegamenuEntryClick);
    });

    if (this.megamenuPanelBack) {
      this.megamenuPanelBack.removeEventListener('click', this.#handleMegamenuPanelBack);
    }

    document.removeEventListener('keydown', this.#handleKeyDown);

    // Nettoyer l'observer
    this.#cleanupStickyObserver();

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  #handleMenuOpen = () => {
    this.openMobileMenu();
  };

  #handleMenuClose = () => {
    this.closeMobileMenu();
  };

  #handleOverlayClick = (event) => {
    // Fermer le menu si on clique sur l'overlay (pas sur le menu lui-même)
    if (event.target === this.mobileMenuOverlay) {
      this.closeMobileMenu();
    }
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.megamenuPanel?.classList.contains('is-open')) {
      this.#closeMegamenuPanel();
      return;
    }
    if (this.mobileMenuOverlay?.classList.contains('is-open')) {
      this.closeMobileMenu();
    }
  };

  #handleMegamenuEntryClick = (event) => {
    const entry = event.currentTarget;
    const id = entry.getAttribute('data-megamenu-id');
    const title = entry.getAttribute('data-megamenu-title');
    if (!id || !this.megamenuPanel) return;
    const contentEl = this.querySelector(`[data-megamenu-content-id="${id}"]`);
    const colorScheme = contentEl?.getAttribute('data-color-scheme') ?? '';
    this.querySelectorAll('[data-ref="megamenu-content"]').forEach((content) => {
      content.classList.toggle('is-visible', content.getAttribute('data-megamenu-content-id') === id);
      content.setAttribute('aria-hidden', content.getAttribute('data-megamenu-content-id') !== id);
    });
    this.megamenuPanel.classList.remove(...Array.from(this.megamenuPanel.classList).filter((c) => c.startsWith('color-')));
    if (colorScheme) this.megamenuPanel.classList.add(`color-${colorScheme}`);
    this.megamenuPanel.classList.add('is-open');
    this.megamenuPanel.setAttribute('aria-hidden', 'false');
    this.megamenuPanelBack?.focus();
  };

  #handleMegamenuPanelBack = () => {
    this.#closeMegamenuPanel();
  };

  #closeMegamenuPanel() {
    if (!this.megamenuPanel) return;
    this.megamenuPanel.classList.remove('is-open');
    this.megamenuPanel.classList.remove(...Array.from(this.megamenuPanel.classList).filter((c) => c.startsWith('color-')));
    this.megamenuPanel.setAttribute('aria-hidden', 'true');
    this.querySelectorAll('[data-ref="megamenu-content"]').forEach((content) => {
      content.classList.remove('is-visible');
      content.setAttribute('aria-hidden', 'true');
    });
  }

  openMobileMenu() {
    // Nettoyer les classes pour éviter les conflits
    this.mobileMenuOverlay.classList.remove('is-closing');
    this.mobileMenuOverlay.classList.add('is-open');
    document.body.classList.add('overflow-hidden');

    // Focus sur le bouton de fermeture pour l'accessibilité
    setTimeout(() => {
      this.menuClose?.focus();
    }, 150);
  }

  closeMobileMenu() {
    this.#closeMegamenuPanel();
    // Ajouter la classe de fermeture pour déclencher l'animation
    this.mobileMenuOverlay.classList.add('is-closing');

    // Attendre la fin de l'animation avant de masquer l'overlay
    setTimeout(() => {
      this.mobileMenuOverlay.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');

      // Remettre le focus sur le bouton menu
      this.menuToggle?.focus();
    }, 125); // Durée légèrement inférieure à --animation-speed pour éviter les décalages
  }

  /**
   * Met à jour la hauteur de l'offset du header, utile pour la position top des megamenus.
   */
  updateHeaderOffsetHeight = () => {
    this.style.setProperty('--header-offset-height', `${this.offsetHeight}px`);
  };

  /**
   * Initialise l'observation du header pour détecter quand il devient sticky
   */
  #initStickyObserver() {
    // Vérifier si le header est configuré comme sticky
    const headerGroup = this.closest('.shopify-section-group-header-group');
    if (!headerGroup || getComputedStyle(headerGroup).position !== 'sticky') {
      return;
    }

    // Calculer la position initiale réelle du header
    document.querySelectorAll('.shopify-section-group-header-group').forEach((section) => {
      if (section === headerGroup) {
        return;
      }
      this.#initialHeaderTop += section.offsetHeight;
    });

    // Créer le gestionnaire de scroll avec throttling
    this.#scrollHandler = this.#throttle(() => {
      this.#checkStickyState();
    }, 10);

    // Ajouter l'écouteur de scroll
    window.addEventListener('scroll', this.#scrollHandler, { passive: true });

    // Vérifier l'état initial
    this.#checkStickyState();
  }

  /**
   * Vérifie l'état sticky du header
   */
  #checkStickyState() {
    if (this.#initialHeaderTop === null) return;

    let offset = this.#initialHeaderTop;
    if (offset === 0) offset = 20;

    // Le header est sticky quand le scroll dépasse sa position initiale
    const isSticky = window.scrollY >= offset;

    this.dataset.stickyState = isSticky ? 'active' : 'inactive';
  }

  /**
   * Throttle function pour optimiser les performances
   */
  #throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  /**
   * Nettoie l'observer lors de la destruction du composant
   */
  #cleanupStickyObserver() {
    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }
    this.#initialHeaderTop = null;
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}
